import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Sales.css";

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_API_URL ||
  "https://techiohisab.com/api";
const SALES_API = `${API_BASE}/sales`;

const todayDate = new Date().toISOString().slice(0, 10);
const emptyEditItem = () => ({
  inventory_id: "",
  quantity: "",
  selling_price: "",
  packaging_material: "",
  packaging_cost: "",
  awbs: [],
});

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value).slice(0, 10) : date.toLocaleDateString("en-IN");
};

const getDraftItems = (draft) => {
  if (Array.isArray(draft?.items)) return draft.items;
  if (Array.isArray(draft?.payload?.items)) return draft.payload.items;
  return [];
};

const getDraftTotal = (draft) =>
  getDraftItems(draft).reduce(
    (sum, item) =>
      sum +
      toNumber(item.quantity) * toNumber(item.selling_price) +
      toNumber(item.quantity) * toNumber(item.packaging_cost),
    0
  );

const getItemLabel = (items, inventory) => {
  if (!items.length) return "-";
  return items
    .map((item) => {
      const product = inventory.find((row) => String(row.id) === String(item.inventory_id));
      return `${product?.name || `Product #${item.inventory_id}`} (${toNumber(item.quantity)})`;
    })
    .join(", ");
};

export default function SalesDrafts() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [movingId, setMovingId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    platform: "DIRECT",
    account_name: "",
    sale_date: todayDate,
    payment_mode: "CASH",
    awb_no: "",
    pack_type: "SINGLE",
    items: [emptyEditItem()],
  });

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      const [draftRes, inventoryRes] = await Promise.all([
        axios.get(`${SALES_API}/drafts`),
        axios.get(`${API_BASE}/inventory`),
      ]);
      const inventoryRows = inventoryRes.data?.inventory || inventoryRes.data?.data || inventoryRes.data || [];
      setDrafts(Array.isArray(draftRes.data) ? draftRes.data : []);
      setInventory(Array.isArray(inventoryRows) ? inventoryRows : []);
    } catch (error) {
      console.error("Sales drafts load error:", error);
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrafts();
  }, []);

  const draftRows = useMemo(
    () =>
      drafts.map((draft) => {
        const items = getDraftItems(draft);
        return {
          ...draft,
          items,
          total_qty: items.reduce((sum, item) => sum + toNumber(item.quantity), 0),
          total_amount: getDraftTotal(draft),
          item_label: getItemLabel(items, inventory),
        };
      }),
    [drafts, inventory]
  );

  const startEdit = (draft) => {
    const payload = draft.payload || {};
    setEditId(draft.id);
    setEditForm({
      platform: draft.platform || payload.platform || "DIRECT",
      account_name: draft.account_name || payload.account_name || "",
      sale_date: String(draft.sale_date || payload.sale_date || todayDate).slice(0, 10),
      payment_mode: draft.payment_mode || payload.payment_mode || "CASH",
      awb_no: draft.awb_no || payload.awb_no || "",
      pack_type: draft.pack_type || payload.pack_type || "SINGLE",
      items: getDraftItems(draft).length ? getDraftItems(draft) : [emptyEditItem()],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateEditItem = (index, key, value) => {
    setEditForm((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = { ...nextItems[index], [key]: value };
      if (key === "inventory_id") {
        const product = inventory.find((row) => String(row.id) === String(value));
        nextItems[index].selling_price =
          product?.price != null ? String(product.price) : nextItems[index].selling_price;
      }
      return { ...prev, items: nextItems };
    });
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editId) return;

    const cleanedItems = editForm.items
      .map((item) => ({
        inventory_id: Number(item.inventory_id || 0),
        quantity: Number(item.quantity || 0),
        selling_price: Number(item.selling_price || 0),
        packaging_material: String(item.packaging_material || "").trim(),
        packaging_cost: Number(item.packaging_cost || 0),
        awbs: Array.isArray(item.awbs) ? item.awbs : [],
      }))
      .filter((item) => item.inventory_id && item.quantity > 0);

    if (!cleanedItems.length) {
      alert("Add at least 1 item");
      return;
    }

    try {
      await axios.put(`${SALES_API}/drafts/${editId}`, {
        ...editForm,
        items: cleanedItems,
        products: cleanedItems,
        sale_type: editForm.pack_type,
        mode: "PACK",
        status: "Packed",
      });
      setEditId(null);
      await fetchDrafts();
      alert("Draft updated successfully");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update draft");
    }
  };

  const deleteDraft = async (draftId) => {
    if (!window.confirm("Delete this draft?")) return;
    try {
      await axios.delete(`${SALES_API}/drafts/${draftId}`);
      await fetchDrafts();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete draft");
    }
  };

  const moveDraft = async (draftId) => {
    setMovingId(draftId);
    try {
      await axios.post(`${SALES_API}/drafts/${draftId}/move`);
      await fetchDrafts();
      alert("Draft moved to sales successfully");
    } catch (error) {
      alert(error.response?.data?.message || "Stock available nahi hai, draft abhi move nahi ho sakta");
    } finally {
      setMovingId(null);
    }
  };

  return (
    <div className="page-container">
      <div className="purchase-top-bar">
        <h2>Sales Drafts</h2>
        <div className="sales-header-actions">
          <button type="button" className="sales-top-btn sales-top-btn-product" onClick={() => navigate("/sales")}>
            Back To Sales
          </button>
        </div>
      </div>

      {editId && (
        <div className="inventory-card sales-draft-edit-card">
          <h3>Edit Draft</h3>
          <form className="inventory-form sales-draft-edit-form" onSubmit={saveEdit}>
            <select
              value={editForm.platform}
              onChange={(event) => setEditForm({ ...editForm, platform: event.target.value })}
            >
              <option value="DIRECT">Direct</option>
              <option value="FLIPKART">Flipkart</option>
              <option value="AMAZON">Amazon</option>
              <option value="MEESHO">Meesho</option>
              <option value="WEBSITE">Website</option>
            </select>
            <input
              placeholder="Account"
              value={editForm.account_name}
              onChange={(event) => setEditForm({ ...editForm, account_name: event.target.value })}
            />
            <input
              type="date"
              value={editForm.sale_date}
              onChange={(event) => setEditForm({ ...editForm, sale_date: event.target.value })}
            />
            <select
              value={editForm.pack_type}
              onChange={(event) => setEditForm({ ...editForm, pack_type: event.target.value })}
            >
              <option value="SINGLE">Single</option>
              <option value="MULTIPLE">Multiple</option>
              <option value="COMBO">Combo</option>
            </select>
            <input
              placeholder="AWB"
              value={editForm.awb_no}
              onChange={(event) => setEditForm({ ...editForm, awb_no: event.target.value })}
            />
          </form>

          <table className="inventory-table sales-drafts-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Available</th>
                <th>Qty</th>
                <th>Rate</th>
                <th>Packaging</th>
                <th>Cost</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {editForm.items.map((item, index) => {
                const product = inventory.find((row) => String(row.id) === String(item.inventory_id));
                return (
                  <tr key={index}>
                    <td>
                      <select
                        value={item.inventory_id}
                        onChange={(event) => updateEditItem(index, "inventory_id", event.target.value)}
                      >
                        <option value="">Select Product</option>
                        {inventory.map((row) => (
                          <option key={row.id} value={row.id}>
                            {row.name} ({row.category})
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{toNumber(product?.stock)}</td>
                    <td>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(event) => updateEditItem(index, "quantity", event.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        value={item.selling_price}
                        onChange={(event) => updateEditItem(index, "selling_price", event.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        value={item.packaging_material || ""}
                        onChange={(event) => updateEditItem(index, "packaging_material", event.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        value={item.packaging_cost || ""}
                        onChange={(event) => updateEditItem(index, "packaging_cost", event.target.value)}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="delete-btn"
                        onClick={() =>
                          setEditForm((prev) => ({
                            ...prev,
                            items:
                              prev.items.length === 1
                                ? prev.items
                                : prev.items.filter((_, itemIndex) => itemIndex !== index),
                          }))
                        }
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="sales-draft-edit-actions">
            <button
              type="button"
              className="add-item-btn"
              onClick={() => setEditForm((prev) => ({ ...prev, items: [...prev.items, emptyEditItem()] }))}
            >
              + Add Item
            </button>
            <button type="button" className="sales-submit-btn" onClick={saveEdit}>
              Update Draft
            </button>
            <button type="button" className="delete-btn" onClick={() => setEditId(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="inventory-card">
        <table className="inventory-table sales-drafts-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Platform</th>
              <th>Account</th>
              <th>Sale Type</th>
              <th>Items</th>
              <th>Qty</th>
              <th>Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="8" className="empty-row">
                  Loading drafts...
                </td>
              </tr>
            )}
            {!loading && draftRows.length === 0 && (
              <tr>
                <td colSpan="8" className="empty-row">
                  No sales drafts found
                </td>
              </tr>
            )}
            {!loading &&
              draftRows.map((draft) => (
                <tr key={draft.id}>
                  <td>{formatDate(draft.sale_date)}</td>
                  <td>{draft.platform}</td>
                  <td>{draft.account_name || "-"}</td>
                  <td>{draft.pack_type || draft.sale_type || "SINGLE"}</td>
                  <td>{draft.item_label}</td>
                  <td>{draft.total_qty}</td>
                  <td>Rs.{draft.total_amount.toFixed(2)}</td>
                  <td className="sales-draft-actions-cell">
                    <button type="button" className="edit-btn" onClick={() => startEdit(draft)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="history-btn"
                      disabled={movingId === draft.id}
                      onClick={() => moveDraft(draft.id)}
                    >
                      {movingId === draft.id ? "Moving..." : "Move To Sales"}
                    </button>
                    <button type="button" className="delete-btn" onClick={() => deleteDraft(draft.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
